import {
  $getRoot,
  type EditorState,
  type LexicalEditor,
  type SerializedEditorState,
} from "lexical";

/**
 * The editor writes no inline styles of its own. One that a stored or shared document carries is
 * applied as CSS by the engine (an overlay over the host's page, a background fetched from
 * anywhere), and the static renderer never draws it, so it is dropped on the way in.
 */
function $dropTextStyles(): void {
  for (const text of $getRoot().getAllTextNodes()) {
    if (text.getStyle() !== "") text.setStyle("");
  }
}

/**
 * Whether the editor reported an error between `begin` and `end`. Only whoever gave the editor its
 * `onError` can know that, so the watch is made there (see `createErrorWatch`).
 */
export interface ErrorWatch {
  begin(): void;
  /** Stops watching and answers with the first error seen, if any. */
  end(): Error | null;
}

/**
 * An `onError` for the editor that can be watched. While nobody is watching, errors go on to
 * `forward`, as they always did. While somebody is, the error is theirs: they asked in order to
 * handle it, and a host told about an error that was recovered from would be told wrong.
 */
export function createErrorWatch(forward: (error: Error) => void): {
  watch: ErrorWatch;
  onError: (error: Error) => void;
} {
  let watching = false;
  let caught: Error | null = null;
  return {
    watch: {
      begin() {
        watching = true;
        caught = null;
      },
      end() {
        watching = false;
        const error = caught;
        caught = null;
        return error;
      },
    },
    onError(error) {
      if (watching) caught ??= error;
      else forward(error);
    },
  };
}

export type ParseResult = { ok: true; state: EditorState } | { ok: false; error: Error };

/**
 * Parses stored content, and says so when the engine could not take all of it.
 *
 * `parseEditorState` does not throw on content it cannot build (a node type that is not
 * registered, most likely): it reports the error to `onError` and hands back what it had built up
 * to that point, which is the document with everything after the failing node missing. Set as the
 * editor's state, that is content silently lost, and the next save writes the loss to storage.
 * So the state is only handed on when nothing was reported while it was parsed.
 */
export function parseContent(
  editor: LexicalEditor,
  content: SerializedEditorState,
  watch: ErrorWatch,
  updateFn?: () => void,
): ParseResult {
  watch.begin();
  let state: EditorState;
  try {
    state = editor.parseEditorState(content, () => {
      $dropTextStyles();
      updateFn?.();
    });
  } catch (error) {
    watch.end();
    return { ok: false, error: error instanceof Error ? error : new Error(String(error)) };
  }
  const error = watch.end();
  return error === null ? { ok: true, state } : { ok: false, error };
}
